import { Controller } from "@hotwired/stimulus"

/**
 * Phase 2.8 Tier 2 — Salesforce-style filter logic editor.
 *
 * Renders a chip-builder dialog ("Match all (AND) / Match any (OR) /
 * Custom") with a raw-text textarea fallback for power users. Writes
 * the resulting expression back into the report-builder's hidden
 * `filter_logic` input via a custom event.
 *
 * Targets:
 *   - input  the textarea (or text input) holding the expression
 *   - mode   the radio group switching between AND/OR/Custom
 *   - filterCount span showing how many filters exist
 *   - error   the inline validation message
 *
 * Values:
 *   - filterCount  number — # filters in the report (clamps refs)
 *
 * Outlets:
 *   - reportBuilder — the parent report-builder controller; we emit
 *     the new logic so the parent persists + re-renders preview.
 */
export default class extends Controller {
  static targets = ["input", "mode", "modeAnd", "modeOr", "modeCustom", "error"]
  static values = {
    filterCount: { type: Number, default: 0 }
  }

  connect() {
    this._syncMode()
  }

  selectMode(event) {
    const mode = event.currentTarget.value
    if (mode === "AND") {
      this._setExpression(this._defaultAnd())
    } else if (mode === "OR") {
      this._setExpression(this._defaultOr())
    }
    // Custom keeps the textarea editable; no auto-write.
  }

  inputChanged() {
    this._validate(this.inputTarget.value)
    this._emit()
  }

  _defaultAnd() {
    const n = Math.max(this.filterCountValue, 1)
    return Array.from({ length: n }, (_, i) => i + 1).join(" AND ")
  }

  _defaultOr() {
    const n = Math.max(this.filterCountValue, 1)
    return Array.from({ length: n }, (_, i) => i + 1).join(" OR ")
  }

  _setExpression(expr) {
    this.inputTarget.value = expr
    this._validate(expr)
    this._emit()
  }

  _syncMode() {
    if (!this.hasInputTarget) return
    const expr = this.inputTarget.value.trim()
    if (expr === "" || expr === this._defaultAnd()) {
      if (this.hasModeAndTarget) this.modeAndTarget.checked = true
    } else if (expr === this._defaultOr()) {
      if (this.hasModeOrTarget) this.modeOrTarget.checked = true
    } else {
      if (this.hasModeCustomTarget) this.modeCustomTarget.checked = true
    }
    this._validate(expr)
  }

  _validate(expression) {
    const errors = this._localValidate(expression)
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = errors.length ? errors[0] : ""
      this.errorTarget.classList.toggle("hidden", errors.length === 0)
    }
    return errors.length === 0
  }

  // Lightweight client-side validator — final word is the server's
  // ReportFilterLogicParser. Catches the obvious problems early so
  // the user sees feedback without a round-trip per keystroke.
  _localValidate(expression) {
    const expr = expression.trim()
    if (expr === "") return []
    if (!/^[\d\s\(\)\bANDORNOT]+$/i.test(expr)) {
      return ["Only digits, AND/OR/NOT, and parentheses are allowed."]
    }
    let depth = 0
    for (const ch of expr) {
      if (ch === "(") depth += 1
      if (ch === ")") depth -= 1
      if (depth < 0) return ["Mismatched closing parenthesis."]
    }
    if (depth !== 0) return ["Unbalanced parentheses."]
    const refs = expr.match(/\d+/g) || []
    const max = this.filterCountValue
    for (const ref of refs) {
      if (parseInt(ref, 10) < 1 || parseInt(ref, 10) > max) {
        return [`Reference ${ref} is out of range (have ${max}).`]
      }
    }
    return []
  }

  _emit() {
    this.element.dispatchEvent(new CustomEvent("filter-logic:changed", {
      bubbles: true,
      detail: { expression: this.inputTarget.value }
    }))
  }
}
